import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { SearchSchemaInput } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { Search, X, Navigation, Clock, Trash2 } from "lucide-react";
import { StarIcon } from "@/components/icons/StarIcon";
import { PlateIcon, CompassIcon, CalendarIcon } from "@/components/icons/NavIcons";
import { EverythingIcon, SeaWaveIcon, HouseBuildingIcon, ShoppingBasketIcon } from "@/components/icons/LaneIcons";
import { MapMarkerPinIcon } from "@/components/icons/MapMarkerPinIcon";
import { SaunaIcon } from "@/components/icons/SaunaIcon";
import { PaletteIcon } from "@/components/icons/PaletteIcon";
import { MicrophoneIcon } from "@/components/icons/MicrophoneIcon";
import { type Item } from "@/data/places";
import { useListedPlaces } from "@/hooks/use-public-places";
import { useMenuDishIndex } from "@/hooks/use-menu-dish-index";
import { useNearestSort } from "@/hooks/useNearestSort";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listImportedEvents } from "@/lib/events/events.functions";
import heroBanner from "@/assets/event-flow.webp";

import { useCity } from "@/context/CityContext";
import { useResolvedCity } from "@/hooks/use-public-cities";
import { useReviewStat } from "@/hooks/use-review-stats";
import { useGeolocation, distanceKm, formatDistance } from "@/hooks/useGeolocation";

import { RegionFilter, parseRegionParam, type RegionValue } from "@/components/RegionFilter";
import { SearchPill } from "@/components/search/SearchPill";
import { REGION_ORDER } from "@/lib/region";
import { isEventExpired } from "@/lib/event-visibility";


type SectionFilter = "all" | "eat" | "do" | "happening";

const SUGGESTIONS: { term: string; Icon: ComponentType<{ className?: string }> }[] = [
  { term: "sauna", Icon: SaunaIcon },
  { term: "breakfast", Icon: PlateIcon },
  { term: "design", Icon: PaletteIcon },
  { term: "rooftop", Icon: HouseBuildingIcon },
  { term: "live music", Icon: MicrophoneIcon },
  { term: "coffee", Icon: PlateIcon },
  { term: "market", Icon: ShoppingBasketIcon },
  { term: "island", Icon: SeaWaveIcon },
  { term: "workout", Icon: CompassIcon },
];
const RECENT_KEY = "wf:recent-searches";
const RECENT_MAX = 8;

function shouldAutoFocus() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(min-width: 768px)").matches;
}

function useRecentSearches() {
  const [items, setItems] = useState<string[]>([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch {}
  }, []);
  const persist = (next: string[]) => {
    setItems(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
  };
  const add = (term: string) => {
    const t = term.trim();
    if (!t) return;
    const next = [t, ...items.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, RECENT_MAX);
    persist(next);
  };
  const remove = (term: string) => persist(items.filter((x) => x !== term));
  const clear = () => persist([]);
  return { items, add, remove, clear };
}

export const Route = createFileRoute("/search")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { q: string; s: SectionFilter; r: RegionValue } => ({
    q: typeof search.q === "string" ? search.q : "",
    s: typeof search.s === "string" && ["all", "eat", "do", "happening"].includes(search.s)
      ? (search.s as SectionFilter)
      : "all",
    r: parseRegionParam(search.r),
  }),
  head: () => ({
    meta: [
      { title: "Search — what" },
      { name: "description", content: "Search restaurants, things to do, and events across Finland." },
    ],
  }),
  component: SearchPage,
});

const SECTION_META: Record<
  Exclude<SectionFilter, "all">,
  { label: string; Icon: ComponentType<{ className?: string }>; hint: string; to: "/eat" | "/do" | "/happening" }
> = {
  eat: { label: "Eat", Icon: PlateIcon, hint: "Tables & bars", to: "/eat" },
  do: { label: "Do", Icon: CompassIcon, hint: "Sauna & sights", to: "/do" },
  happening: { label: "Events", Icon: CalendarIcon, hint: "This week", to: "/happening" },
};

function SearchPage() {
  const { q, s, r } = Route.useSearch();
  const { city } = useCity();
  const cityName = useResolvedCity(city).name;
  const navigate = useNavigate();
  const [input, setInput] = useState(q);
  useEffect(() => setInput(q), [q]);
  const ITEMS = useListedPlaces();
  const dishIndex = useMenuDishIndex();
  const recent = useRecentSearches();

  // Live URL sync (debounced) — types in the box → results update in real time.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (input === q) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      navigate({ to: "/search", search: { q: input.trim(), s, r }, replace: true });
    }, 220);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  // Persist non-empty queries to recent history after they settle in the URL.
  useEffect(() => {
    if (q.trim().length >= 2) {
      const t = setTimeout(() => recent.add(q.trim()), 600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Pull in scraped/imported events (Lippu etc.) so newly synced happenings
  // are searchable the moment they appear in the admin DB.
  const fetchImported = useServerFn(listImportedEvents);
  const { data: importedData } = useQuery({
    queryKey: ["search-imported-events"],
    queryFn: () => fetchImported({ data: { sort: "upcoming", limit: 100, offset: 0 } }),
    staleTime: 60_000,
  });
  const importedItems: Item[] = useMemo(() => {
    const events = importedData?.events ?? [];
    return events
      .filter((e) => Boolean(e.start_date))
      .map((e): Item => ({
        id: `imported-${e.slug}`,
        section: "happening",
        city: (e.city ?? city) as Item["city"],
        name: e.title,
        category: e.category ?? e.genre ?? "Event",
        area: e.venue ?? e.city ?? "",
        description: e.short_description ?? "",
        image: e.event_image || e.gallery_images?.[0] || heroBanner,
        tags: [],
        date: e.start_date
          ? new Date(e.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
          : undefined,
        startsAt: e.start_date ?? undefined,
        endsAt: e.end_date ?? undefined,
        link: e.ticket_url ?? undefined,
        price: e.price_from ?? e.price ?? undefined,
        lat: e.lat ?? undefined,
        lng: e.lng ?? undefined,
      }));
  }, [importedData, city]);

  // Combined searchable pool: curated places + live imported events.
  // Dedupe by id so a Lippu event already promoted into `places` doesn't duplicate.
  // Finished events are dropped here so search never surfaces an ended happening.
  const pool = useMemo(() => {
    const seen = new Set<string>();
    const out: Item[] = [];
    for (const it of [...ITEMS, ...importedItems]) {
      if (!it || seen.has(it.id)) continue;
      if (it.section === "happening" && isEventExpired(it as any)) continue;
      seen.add(it.id);
      out.push(it);
    }
    return out;
  }, [ITEMS, importedItems]);

  // Advanced matching: tokenize the query, score each item per token across
  // weighted fields (name > area/category > tags > description). Supports
  // partial words and multi-word queries like "thai ramen kallio".
  const tokens = useMemo(
    () => q.trim().toLowerCase().split(/\s+/).filter((t: string) => t.length > 0),
    [q],
  );
  const allResults = useMemo(() => {
    if (tokens.length === 0) return [] as Item[];
    type Scored = { item: Item; score: number };
    const scored: Scored[] = [];
    for (const i of pool) {
      if (!i) continue;
      const name = (i.name ?? "").toLowerCase();
      const area = (i.area ?? "").toLowerCase();
      const cat = `${i.category ?? ""} ${i.categorySlug ?? ""}`.toLowerCase();
      const desc = (i.description ?? "").toLowerCase();
      const tagStr = (i.tags ?? []).join(" ").toLowerCase();
      // Dish names from the place's menu — so "carbonara" finds the kitchen
      // that cooks it, not just restaurants with it in the description.
      const dishes = (i.dbId ? dishIndex[i.dbId] : "") ?? "";
      let score = 0;
      let matchedAll = true;
      for (const t of tokens) {
        let hit = 0;
        if (name === t) hit += 100;
        else if (name.startsWith(t)) hit += 60;
        else if (name.includes(t)) hit += 40;
        if (area.includes(t)) hit += 20;
        if (cat.includes(t)) hit += 18;
        if (tagStr.includes(t)) hit += 14;
        if (dishes.includes(t)) hit += 24;
        if (desc.includes(t)) hit += 6;
        if (hit === 0) { matchedAll = false; break; }
        score += hit;
      }
      if (matchedAll) scored.push({ item: i, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.item);
  }, [pool, tokens, dishIndex]);

  const regionCounts = useMemo(() => {
    const c: Record<RegionValue, number> = { all: allResults.length, lapland: 0, lakeland: 0, archipelago: 0, coast: 0 };
    for (const it of allResults) {
      const k = it.region as RegionValue | undefined;
      if (k && (REGION_ORDER as string[]).includes(k)) c[k] = (c[k] ?? 0) + 1;
    }
    return c;
  }, [allResults]);

  const regionScoped = r === "all" ? allResults : allResults.filter((it) => it.region === r);

  const counts = useMemo(() => ({
    all: regionScoped.length,
    eat: regionScoped.filter((it) => it.section === "eat").length,
    do: regionScoped.filter((it) => it.section === "do").length,
    happening: regionScoped.filter((it) => it.section === "happening").length,
  }), [regionScoped]);

  const results = s === "all" ? regionScoped : regionScoped.filter((it) => it.section === s);
  const byCityRaw = results.filter((it) => Boolean(it?.city) && it.city === city);
  const elsewhereRaw = results.filter((it) => Boolean(it?.city) && it.city !== city);
  const { items: byCity } = useNearestSort(byCityRaw);
  const { items: elsewhere } = useNearestSort(elsewhereRaw);


  const submit = (value: string) => navigate({ to: "/search", search: { q: value.trim(), s, r } });
  const setSection = (next: SectionFilter) => navigate({ to: "/search", search: { q, s: next, r } });
  const setRegion = (next: RegionValue) => navigate({ to: "/search", search: { q, s, r: next }, replace: true });

  return (
    <div className="min-h-screen bg-white">
      <div className="relative mx-auto max-w-7xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
        <header className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3.5 sm:gap-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-navy text-white shadow-[0_12px_28px_-12px_oklch(0.22_0.09_260/0.75)] sm:size-[3.75rem]">
              <Search className="size-5 sm:size-6" strokeWidth={2.25} />
            </span>
            <div className="min-w-0">
              <p className="font-body text-[10px] font-semibold uppercase tracking-[0.22em] text-navy/45 sm:text-[11px]">
                {cityName}
              </p>
              <h1 className="mt-0.5 font-display text-[2.35rem] font-extrabold leading-[0.84] tracking-tighter text-navy lowercase sm:text-[3.4rem]">
                {q ? "results" : "search"}
              </h1>
            </div>
          </div>
          {q ? (
            <p className="mt-3 max-w-xl text-[14px] leading-snug text-navy/60 sm:text-[15px]">
              Showing matches for <span className="font-semibold text-navy">“{q}”</span>
            </p>
          ) : (
            <p className="mt-3 max-w-xl text-[14px] leading-snug text-navy/60 sm:text-[15px]">
              Restaurants, things to do and events — handpicked across Finland.
            </p>
          )}
        </header>

        <div className="sticky top-2 z-20 sm:top-3">
          <SearchPill
            value={input}
            onChange={(v) => setInput(v)}
            onSubmit={(v) => submit(v)}
            autoFocus={shouldAutoFocus()}
            placeholder="Search destinations, places or experiences"
          />

          {q ? (
            <p className="mt-2 px-1 font-mono text-[10px] uppercase tracking-[0.18em] text-navy/45">
              {counts.all} result{counts.all === 1 ? "" : "s"}
            </p>
          ) : null}
        </div>

        {q && allResults.length > 0 ? (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1 sm:mt-5 sm:flex-wrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <FilterChip active={s === "all"} onClick={() => setSection("all")} label="All" count={counts.all} Icon={EverythingIcon} />
            <FilterChip active={s === "eat"} onClick={() => setSection("eat")} label="Eat" count={counts.eat} Icon={PlateIcon} />
            <FilterChip active={s === "do"} onClick={() => setSection("do")} label="Do" count={counts.do} Icon={CompassIcon} />
            <FilterChip active={s === "happening"} onClick={() => setSection("happening")} label="Events" count={counts.happening} Icon={CalendarIcon} />
          </div>
        ) : null}

        {q && allResults.length > 0 ? (
          <div className="mt-2 sm:mt-3">
            <RegionFilter value={r} onChange={setRegion} counts={regionCounts} />
          </div>
        ) : null}

        {!q ? (
          <EmptyState
            onPick={(term) => { setInput(term); submit(term); }}
            recent={recent.items}
            onRemoveRecent={recent.remove}
            onClearRecent={recent.clear}
          />
        ) : results.length === 0 ? (
          <div className="mt-10 rounded-[1.5rem] bg-white px-6 py-12 text-center ring-1 ring-navy/10 sm:mt-14 sm:px-8 sm:py-16">
            <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-navy text-white">
              <Search className="size-5" />
            </span>
            <p className="mt-4 font-heading text-2xl text-navy sm:text-3xl">No matches</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-navy/55">
              Try another word, or ask what AI from the chat button.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.slice(0, 5).map(({ term, Icon }) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => { setInput(term); submit(term); }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-navy ring-1 ring-navy/12 hover:bg-navy hover:text-white"
                >
                  <Icon className="size-3.5" />
                  {term}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-10 sm:mt-10 sm:space-y-12">
            <ResultGroup title={`In ${cityName}`} items={byCity} />
            {elsewhere.length > 0 ? <ResultGroup title="Elsewhere in Finland" items={elsewhere} /> : null}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  Icon?: ComponentType<{ className?: string }>;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold ring-1 transition-colors ${
        active ? "bg-navy text-white ring-navy" : "bg-white text-navy/75 ring-navy/12 hover:ring-navy/30"
      }`}
    >
      {Icon ? <Icon className="size-3.5" /> : null}
      <span>{label}</span>
      <span className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] tabular-nums ${active ? "bg-white/15" : "bg-navy/5"}`}>
        {count}
      </span>
    </button>
  );
}

function EmptyState({
  onPick,
  recent,
  onRemoveRecent,
  onClearRecent,
}: {
  onPick: (term: string) => void;
  recent: string[];
  onRemoveRecent: (term: string) => void;
  onClearRecent: () => void;
}) {
  return (
    <div className="mt-8 space-y-5 sm:mt-10">
      {recent.length > 0 ? (
        <section className="rounded-[1.5rem] bg-white p-5 ring-1 ring-navy/10 sm:p-6">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/50">
              <Clock className="size-3.5" /> Recent
            </p>
            <button
              type="button"
              onClick={onClearRecent}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy/50 hover:text-navy"
            >
              <Trash2 className="size-3.5" /> Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {recent.map((term) => (
              <span
                key={term}
                className="inline-flex items-center gap-1 rounded-full bg-white pl-3 pr-1 py-1 text-sm text-navy ring-1 ring-navy/12"
              >
                <button type="button" onClick={() => onPick(term)} className="max-w-[180px] truncate">
                  {term}
                </button>
                <button
                  type="button"
                  onClick={() => onRemoveRecent(term)}
                  aria-label={`Remove ${term}`}
                  className="grid size-6 place-items-center rounded-full text-navy/40 hover:bg-navy/8 hover:text-navy"
                >
                  <X className="size-3.5" />
                </button>
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <section className="rounded-[1.5rem] bg-white p-5 ring-1 ring-navy/10 sm:p-6">
        <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-navy/50">Try searching</p>
        <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
          {SUGGESTIONS.map(({ term, Icon }) => (
            <button
              key={term}
              type="button"
              onClick={() => onPick(term)}
              className="inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full bg-white px-2 py-2.5 text-[13px] font-semibold text-navy ring-1 ring-navy/12 transition-colors hover:bg-navy hover:text-white sm:px-3"
            >
              <Icon className="size-3.5 shrink-0" />
              <span className="truncate">{term}</span>
            </button>
          ))}
        </div>
      </section>

    </div>
  );
}

function ResultGroup({ title, items }: { title: string; items: Item[] }) {
  if (items.length === 0) return null;
  return (
    <section>
      <div className="mb-4 flex items-end justify-between gap-3">
        <h2 className="font-heading text-[22px] leading-none tracking-tight text-navy sm:text-[26px]">{title}</h2>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-navy/45">
          {items.length} result{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
        {items.map((it) => {
          const meta = SECTION_META[it.section as Exclude<SectionFilter, "all">];
          return (
            <SearchResultCard
              key={it.id}
              item={it}
              sectionLabel={meta?.label ?? it.section}
              SectionIcon={meta?.Icon}
            />
          );
        })}
      </div>
    </section>
  );
}

function SearchResultCard({
  item,
  sectionLabel,
  SectionIcon,
}: {
  item: Item;
  sectionLabel: string;
  SectionIcon?: ComponentType<{ className?: string }>;
}) {
  const stat = useReviewStat(item.id);
  const hasReviews = stat.count >= 5;
  const { state: geo } = useGeolocation(true);
  const dist =
    geo.status === "granted" && item.lat != null && item.lng != null
      ? distanceKm(geo, { lat: item.lat, lng: item.lng })
      : null;

  return (
    <Link
      to="/place/$id"
      params={{ id: item.id }}
      className="group overflow-hidden rounded-[1.35rem] bg-white ring-1 ring-navy/10 transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(15,23,42,0.28)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={item.image}
          alt={`Photo of ${item.name}${item.city ? ` in ${item.city}` : ""} — ${sectionLabel} on what Finland`}
          loading="lazy"
          className="size-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/92 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-navy backdrop-blur">
          {SectionIcon ? <SectionIcon className="size-3" /> : null}
          {sectionLabel}
        </span>
        {hasReviews ? (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[11px] font-semibold text-navy">
            <StarIcon className="size-3 text-[var(--star)]" /> {stat.avg.toFixed(1)}
          </span>
        ) : null}
      </div>
      <div className="px-4 py-3.5">
        <p className="line-clamp-1 font-heading text-[17px] leading-snug tracking-tight text-navy">{item.name}</p>
        <p className="mt-1.5 flex items-center gap-1 truncate text-[12px] text-navy/55">
          <MapMarkerPinIcon className="size-3 shrink-0" />
          <span className="truncate">{[item.area, item.category].filter(Boolean).join(" · ")}</span>
        </p>
        {dist != null ? (
          <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-navy/65">
            <Navigation className="size-3 shrink-0 text-teal" />
            <span className="tabular-nums">{formatDistance(dist)}</span>
          </p>
        ) : null}
      </div>
    </Link>
  );
}
